<script>
  import MediaOutletNav from '$lib/components/MediaOutletNav/index.svelte';
  import Canvas from './Canvas.svelte';
  import ChartSVG from './ChartSVG.svelte';
  import AxisSVG from './AxisSVG.svelte';
  import { scaleLinear, scaleTime } from 'd3-scale';
  import { activeMediaOutlet } from '$lib/states/index.svelte';
  import { setDateDomain } from '$lib/utils';
  import { hiddenFromPollChart } from '$lib/constants/crosswalk';
  import Note from './Note.svelte';

  let { block, structuredData, latestPollDate } = $props();

  const hiddenPartySet = new Set(hiddenFromPollChart);

  // drops parties hidden from this chart (lines/dots/hover) from one
  // {data, trends, extents} slice — used for both the overall dataset and
  // each media-outlet-filtered slice below
  const omitHidden = (slice) => ({
    data: slice.data.filter((d) => !hiddenPartySet.has(d.party)),
    trends: Object.fromEntries(
      Object.entries(slice.trends).filter(([key]) => !hiddenPartySet.has(key))
    ),
    extents: Object.fromEntries(
      Object.entries(slice.extents).filter(([key]) => !hiddenPartySet.has(key))
    ),
  });

  const filtered = omitHidden(structuredData);

  // overall data, each dot is the performance of one party in one poll
  let data = $state(filtered.data);

  // trend line for each party
  let trends = $state(filtered.trends);

  // extents of polls by party
  let extents = $state(filtered.extents);

  let mediaOutletList = $derived(
    Object.keys(structuredData.byMediaOutlet)
      .map((slug) => ({ slug, name: structuredData.byMediaOutlet[slug].name }))
      .sort((a, b) => a.name.localeCompare(b.name))
  );

  const breakpoint = 500;

  let w = $state();
  let h = $state();
  let hoveredParty = $state(null);

  let size = $derived(w < breakpoint ? 'sm' : 'lg');

  const margin = $state({ top: 16, left: 4, right: 100, bottom: 90 });

  let chartwidth = $derived(w - margin.left - margin.right);
  let chartheight = $derived(h - margin.top - margin.bottom);

  // Fixed at 40 seats regardless of data or media-outlet filter, so the axis
  // doesn't jump around as the user switches filters.
  const Y_MAX = 40;
  let yScale = $derived(
    scaleLinear().domain([Y_MAX, 0]).range([0, chartheight])
  );
  let dateExtent = $state();
  let xScale = $state();

  $effect(() => {
    if (dateExtent && dateExtent.length === 2) {
      xScale = scaleTime().domain(dateExtent).range([0, chartwidth]);
    }
  });

  // updates based on width
  $effect(() => {
    if (size === 'sm') {
      margin.right = 55;
      margin.bottom = 50;
    } else {
      margin.right = 100;
      margin.bottom = 90;
    }
  });

  const updateData = (newMediaOutlet) => {
    const mediaOutlet =
      newMediaOutlet !== 'all' ? structuredData.byMediaOutlet[newMediaOutlet] : null;
    const next = omitHidden(mediaOutlet || structuredData);
    data = next.data;
    trends = next.trends;
    extents = next.extents;
  };

  $effect(() => {
    updateData(activeMediaOutlet.value);
  });

  $effect(() => {
    dateExtent = setDateDomain(data);
  });
</script>

<MediaOutletNav mediaOutlets={mediaOutletList} />
<div
  class="poll-chart chart-wrapper"
  bind:clientWidth={w}
  bind:clientHeight={h}
>
  <AxisSVG {w} {h} {chartwidth} {margin} {xScale} {yScale} {dateExtent} />
  <Canvas
    {w}
    {h}
    {margin}
    {data}
    {xScale}
    {yScale}
    {activeMediaOutlet}
    {hoveredParty}
    {trends}
  />
  <ChartSVG
    {w}
    {h}
    {chartwidth}
    {chartheight}
    {margin}
    {trends}
    {extents}
    {xScale}
    {yScale}
    {block}
    bind:hoveredParty
  />
  <div class="notewrap-lg notewrap" style="top: {chartheight + 45}px;">
    <Note {block} {latestPollDate} />
  </div>
</div>

<div class="notewrap-sm notewrap">
  <Note {block} {latestPollDate} />
</div>

<style lang="scss">
  .chart-wrapper {
    width: 100%;
    height: 580px;
    position: relative;
    // margin-bottom: 50px;
    @media (max-width: 530px) {
      height: 475px;
      // On mobile the note sits in normal flow right after this wrapper
      // (`.notewrap-sm`) and brings its own top spacing via `Note.svelte`'s
      // `fmt-2` — this margin would double up with that and leave a huge gap.
      margin-bottom: 0;
    }
  }
  .notewrap-sm {
    position: relative;
  }
  .notewrap-lg {
    position: absolute;
  }
  .notewrap {
    width: 100%;
  }

  :global {
    .poll-chart {
      .chart-svg,
      canvas {
        position: absolute;
        top: 0px;
        left: 0px;
        width: 100%;
        height: 100%;
      }
    }
  }
  @media screen and (min-width: 530px) {
    .notewrap-sm {
      display: none;
    }
  }

  @media screen and (max-width: 529px) {
    .notewrap-lg {
      display: none;
    }
  }
</style>
